import * as Sentry from '@sentry/react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { PhoneNumber } from 'google-libphonenumber';
import { useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { signInPhoneNumber } from '@/api/functions/authQuery';
import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import PhoneInput from '@/components/inputs/PhoneInput';
import LoginHeader from '@/components/LoginHeader';
import TermsPrivacyText from '@/components/TermsPrivacyText';
import Text from '@/components/Text';
import TRBShowBanner from '@/components/trbShow/TRBShowBanner';
import { SIGN_IN_BUTTON_BOTTOM } from '@/constants/layout';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useColor } from '@/hooks/useColor';
import { i18n } from '@/utils/i18n';
import { handlePhoneAuthError } from '@/utils/loginErrorsHandler';

const IndividualSignIn = () => {
  const router = useRouter();
  const colors = useColor();

  const params = useLocalSearchParams();
  const buyingShow = params.buyingShow === 'true';

  //Locales
  const titleText = i18n.t('sign_in.individual.phone_sign_in.title');
  const buttonText = i18n.t('sign_in.individual.phone_sign_in.button');
  const smsText = i18n.t('sign_in.individual.phone_sign_in.sms');

  const [phoneNumber, setPhoneNumber] = useState<PhoneNumber>();
  const [resolvingCaptcha, setResolvingCaptcha] = useState(false);

  const { dispatch: dispatchAuth } = useAuth();

  const { showError } = useEndFlowModal();

  const onSend = () => {
    setResolvingCaptcha(true);
    const fullNumber = `+${phoneNumber?.getCountryCode()}${phoneNumber?.getNationalNumber()}`;

    signInPhoneNumber({ phoneNumber: fullNumber, captchaTag: 'recaptcha-container' })
      .then((data) => {
        if (phoneNumber) {
          dispatchAuth({
            type: 'SET_SIGN_UP_DATA',
            preferenceId: '',
            signUpData: {
              confirmation: data
            }
          });
          router.navigate({
            pathname: '/login/individual/verifyCode',
            params: {
              phoneNumber: fullNumber,
              ...(buyingShow && { buyingShow: 'true' })
            }
          });
        }
      })
      .catch((e) => {
        setResolvingCaptcha(false);
        const { title, message } = handlePhoneAuthError(e);
        showError({ title, message });
        console.error('Phone number sign in failed', e);
        Sentry.captureException(e);
      });
  };

  return (
    <FormWrapper keyboardVerticalOffset={0}>
      <SafeAreaView style={styles.content}>
        <View style={styles.container}>
          <LoginHeader />
          {buyingShow && (
            <View style={styles.signInToBuyContainer}>
              <TRBShowBanner disabled />
              <Text fontWeight="600" style={styles.signInToBuyText}>
                {i18n.t('trb_show.sign_in_to_buy')}
              </Text>
            </View>
          )}
          <Text style={[styles.title, { color: colors.titleText }]} fontWeight="700">
            {titleText}
          </Text>
          <View style={styles.phoneLoginContainer}>
            <PhoneInput onNumberChange={(number) => setPhoneNumber(number)} />
          </View>

          <Text style={styles.rates}>{smsText}</Text>
          <TermsPrivacyText />
        </View>
        {Platform.OS === 'web' && (
          <div id="recaptcha-container" style={{ display: resolvingCaptcha ? 'flex' : 'none' }} />
        )}
        {Platform.OS === 'web' && resolvingCaptcha && (
          <Text style={styles.captchaHelper}>{i18n.t('mix.recaptcha_helper')}</Text>
        )}
        <BrandedButton
          style={styles.button}
          onPress={onSend}
          loading={resolvingCaptcha}
          disabled={resolvingCaptcha || !phoneNumber}>
          {buttonText}
        </BrandedButton>
      </SafeAreaView>
    </FormWrapper>
  );
};

export default IndividualSignIn;

const styles = StyleSheet.create({
  content: {
    flex: 1
  },

  container: {
    flex: 1
  },

  title: {
    fontSize: 22,
    marginBottom: 25
  },

  signInToBuyContainer: {
    flexDirection: 'column',
    gap: 5,
    marginBottom: 25
  },

  signInToBuyText: {
    color: 'black',
    fontSize: 14
  },

  rates: {
    marginTop: 25
  },

  button: {
    marginBottom: SIGN_IN_BUTTON_BOTTOM
  },

  phoneLoginContainer: {
    position: 'relative',
    zIndex: 1000
  },

  captchaHelper: {
    paddingVertical: 10
  }
});
